import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import {
  clearDocuments,
  fetchDocuments,
  selectAllDocuments,
  selectDocumentsPage,
  setDocumentsPage,
} from '../../../slices/documents/documentsSlice';
import { selectUser } from '../../../slices/user/userSlice';
import { DOCUMENT_CATEGORIES } from '../types';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import {
  fetchBusinessProfileById,
  fetchBusinessProfileContactById,
} from '../../../slices/business-profile/businessProfileSlice';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import { ROUTES } from '../../../common/constants/routes';
import { DocumentVaultSearchData } from '../../../models/document.vault.item.model';
import {
  DOCUMENTS_PER_PAGE,
  parseVaultCategory,
  readVaultQuery,
  VaultQueryState,
  writeVaultQuery,
} from './vaultQueryParams';
import { readProfileQuery, useProfileDocumentsQuery } from './useProfileDocumentsQuery';

const DOCUMENTS_POLLING_INTERVAL = 60000;
const DOCUMENTS_BUSY_STATUSES = ['loading', 'uploading', 'updating', 'deleting', 'deleteLoading'];

const useDocuments = (categoryValue?: string, workflow?: string, profileQuery?: number) => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const taskIds = useAppSelector((state: RootState) => state.tasks.taskIds);
  const documentsCount = useAppSelector((state) => state.documents.documentsCount);
  const documentsPage = useAppSelector(selectDocumentsPage);
  const documentItems = useAppSelector(selectAllDocuments);
  const documentContractsLoading = useAppSelector((state) => state.documentContracts.status);
  const documentUploadsLoading = useAppSelector((state) => state.documents.status);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  const customersLoading = useAppSelector((state) => state.customers.status);
  const suppliersLoading = useAppSelector((state) => state.suppliers.status);
  const businessProfileLoading = useAppSelector((state) => state.businessProfile.status);
  const currentUser = useAppSelector(selectUser);
  const [params, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const location = useLocation();
  const path = location.pathname;
  const profilesLocations = [
    ROUTES.EMPLOYEE_PROFILES,
    ROUTES.SUPPLIER_PROFILES,
    ROUTES.CUSTOMER_PROFILES,
    ROUTES.BUSINESS_PROFILE,
  ];
  const isProfilePage = profilesLocations.some((el) => path.includes(el));

  /*
   * On the Document Vault page the URL query is the single source of truth for the view state
   * (?category=customers&page=3&view=card). Every category / page / view change creates a history
   * entry, so the browser Back button from a document details page restores the exact view.
   * Profile pages reuse this hook with a fixed category; their view state (workflow, card/list
   * view, page) lives in the URL too - see useProfileDocumentsQuery.
   */
  const isVaultPage = !isProfilePage && !categoryValue;
  const {
    category: vaultCategory,
    page: vaultPage,
    cardView: vaultCardView,
  } = readVaultQuery(params);

  const {
    cardView: profileCardView,
    page: profilePage,
    updateProfileQuery,
  } = useProfileDocumentsQuery(isProfilePage);

  const [localCardView, setLocalCardView] = useState<boolean>(true);
  const [localActiveTab, setLocalActiveTab] = useState<DOCUMENT_CATEGORIES>('All');
  const [localShowWorkflowBreadcrumbs, setLocalShowWorkflowBreadcrumbs] = useState<boolean>(false);

  const activeTab: DOCUMENT_CATEGORIES = isVaultPage ? vaultCategory : localActiveTab;
  const cardView: boolean = isVaultPage
    ? vaultCardView
    : isProfilePage
      ? profileCardView
      : localCardView;
  const currentPage: number = isVaultPage ? vaultPage : isProfilePage ? profilePage : documentsPage;
  // On profile pages the documents of a workflow are shown whenever a workflow is in the URL.
  const showWorkflowBreadcrumbs: boolean = isProfilePage
    ? Boolean(workflow)
    : localShowWorkflowBreadcrumbs;

  // Latest query params, so async callbacks never write a stale URL.
  const paramsRef = useRef(params);
  const isMountedRef = useRef(false);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateVaultQuery = useCallback(
    (changes: Partial<VaultQueryState>, options?: { replace?: boolean }) => {
      if (!isMountedRef.current) {
        return;
      }
      const current = paramsRef.current;
      const next = writeVaultQuery(current, { ...readVaultQuery(current), ...changes });
      if (next.toString() !== current.toString()) {
        paramsRef.current = next;
        setSearchParams(next, { replace: options?.replace ?? false });
      }
    },
    [setSearchParams]
  );

  const setActiveTab: Dispatch<SetStateAction<DOCUMENT_CATEGORIES>> = useCallback(
    (value: SetStateAction<DOCUMENT_CATEGORIES>) => {
      if (!isVaultPage) {
        setLocalActiveTab(value);
        return;
      }
      const nextTab =
        typeof value === 'function' ? value(readVaultQuery(paramsRef.current).category) : value;
      updateVaultQuery({ category: parseVaultCategory(nextTab), page: 1 });
    },
    [isVaultPage, updateVaultQuery]
  );

  const setCardView: Dispatch<SetStateAction<boolean>> = useCallback(
    (value: SetStateAction<boolean>) => {
      if (isProfilePage) {
        const nextCardView = typeof value === 'function' ? value(profileCardView) : value;
        // A workflow can be chosen only in the card view, so a view switch starts from the workflow cards.
        updateProfileQuery({ cardView: nextCardView, workflow: null, page: 1 });
        return;
      }
      if (!isVaultPage) {
        setLocalCardView(value);
        return;
      }
      const nextCardView =
        typeof value === 'function' ? value(readVaultQuery(paramsRef.current).cardView) : value;
      updateVaultQuery({ cardView: nextCardView, page: 1 });
    },
    [isProfilePage, isVaultPage, profileCardView, updateProfileQuery, updateVaultQuery]
  );

  const setShowWorkflowBreadcrumbs: Dispatch<SetStateAction<boolean>> = useCallback(
    (value: SetStateAction<boolean>) => {
      // Derived from the workflow in the URL on profile pages - setting the workflow is enough.
      if (!isProfilePage) {
        setLocalShowWorkflowBreadcrumbs(value);
      }
    },
    [isProfilePage]
  );

  const getNextPage = () => {
    if (isVaultPage) {
      updateVaultQuery({ page: currentPage + 1 });
      return;
    }
    if (isProfilePage) {
      updateProfileQuery({ page: currentPage + 1 });
      return;
    }
    //dispatch(clearDocuments());
    //dispatch(clearGeneratedDocuments());
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryValue
            ? categoryValue
            : activeTab === 'All'
              ? ''
              : activeTab.toLowerCase(),
          page: documentsPage + 1,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    );
    dispatch(setDocumentsPage(documentsPage + 1));
  };

  const getRequestedPage = (passedPage: number, category?: string) => {
    if (isVaultPage) {
      updateVaultQuery({
        page: passedPage,
        ...(category ? { category: parseVaultCategory(category) } : {}),
      });
      return;
    }
    if (isProfilePage) {
      updateProfileQuery({ page: passedPage });
      return;
    }
    const categoryToUse = category || categoryValue || activeTab;
    //dispatch(clearDocuments());
    //dispatch(clearGeneratedDocuments());
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryToUse === 'All' ? '' : categoryToUse.toLowerCase(),
          page: passedPage,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    );
    dispatch(setDocumentsPage(passedPage));
  };

  const getPrevPage = () => {
    if (isVaultPage) {
      updateVaultQuery({ page: Math.max(1, currentPage - 1) });
      return;
    }
    if (isProfilePage) {
      updateProfileQuery({ page: Math.max(1, currentPage - 1) });
      return;
    }
    //dispatch(clearDocuments());
    //dispatch(clearGeneratedDocuments());
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryValue
            ? categoryValue
            : activeTab === 'All'
              ? ''
              : activeTab.toLowerCase(),
          page: documentsPage - 1,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    );
    dispatch(setDocumentsPage(documentsPage - 1));
  };

  /*const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.updated)) - Number(new Date(a.updated));
  };*/

  const documents = [...documentItems]; /*.sort((a, b) => sortByDate(a, b))*/

  const handleTabChange = (tab: DOCUMENT_CATEGORIES) => {
    if (isVaultPage) {
      updateVaultQuery({ category: tab, page: 1 });
      return;
    }
    dispatch(setDocumentsPage(1));
    setLocalActiveTab(tab);
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const workflowRequest = useMemo(() => {
    return workflow
      ? {
          workflow: workflow !== t('labels.generalUnassigned') ? workflow : String(null),
        }
      : {};
  }, [workflow, t]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const profileQueryRequest = useMemo(() => {
    return profileQuery ? { profile: profileQuery } : {};
  }, [profileQuery]);

  const isProfileListVisible = cardView ? Boolean(workflow) : true;
  const lastProfileListKeyRef = useRef<string | null>(null);

  // Profile pages: fetch whatever the URL describes (workflow / list view + page).
  useEffect(() => {
    if (!isProfilePage || !isProfileListVisible) {
      lastProfileListKeyRef.current = null;
      return;
    }
    if (!currentUser.id || !categoryValue || !taskIds || taskIds.length > 0) {
      return;
    }
    const listKey = `${cardView ? 'card' : 'list'}|${workflow ?? ''}`;
    if (lastProfileListKeyRef.current !== listKey) {
      // Do not show documents of the previous workflow while the new ones are loading.
      dispatch(clearDocuments());
      lastProfileListKeyRef.current = listKey;
    }
    dispatch(setDocumentsPage(profilePage));

    const requestedPage = profilePage;
    const requestedWorkflow = workflow ?? null;
    const isStillRequestedView = () => {
      const current = readProfileQuery(new URLSearchParams(window.location.search));
      return current.page === requestedPage && current.workflow === requestedWorkflow;
    };

    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryValue.toLowerCase(),
          page: requestedPage,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    )
      .unwrap()
      .then((result) => {
        // The page from the URL no longer exists (e.g. documents were deleted) - go to the last one.
        if (requestedPage > 1 && (result.results?.length ?? 0) === 0 && isStillRequestedView()) {
          const lastPage = Math.max(1, Math.ceil((result.count || 0) / DOCUMENTS_PER_PAGE));
          if (lastPage < requestedPage) {
            updateProfileQuery({ page: lastPage }, { replace: true });
          }
        }
      })
      .catch(() => {
        // Out-of-range pages are rejected by the API - fall back to the first page.
        if (requestedPage > 1 && isStillRequestedView()) {
          updateProfileQuery({ page: 1 }, { replace: true });
        }
      });
  }, [
    isProfilePage,
    isProfileListVisible,
    currentUser.id,
    categoryValue,
    taskIds,
    cardView,
    workflow,
    profilePage,
    workflowRequest,
    profileQueryRequest,
    updateProfileQuery,
    dispatch,
  ]);

  const isVaultListVisible = !(vaultCategory === 'All' && vaultCardView);
  const lastVaultCategoryRef = useRef<DOCUMENT_CATEGORIES | null>(null);

  // Document Vault: fetch whatever the URL describes (category + page).
  useEffect(() => {
    if (!isVaultPage || !isVaultListVisible) {
      lastVaultCategoryRef.current = null;
      return;
    }
    if (!currentUser.id || !taskIds || taskIds.length > 0) {
      return;
    }
    if (lastVaultCategoryRef.current !== vaultCategory) {
      // Do not show documents of the previous category while the new ones are loading.
      dispatch(clearDocuments());
      lastVaultCategoryRef.current = vaultCategory;
    }
    dispatch(setDocumentsPage(vaultPage));

    const requestedCategory = vaultCategory;
    const requestedPage = vaultPage;
    const isStillRequestedView = () => {
      const current = readVaultQuery(paramsRef.current);
      return current.category === requestedCategory && current.page === requestedPage;
    };

    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: requestedCategory === 'All' ? '' : requestedCategory.toLowerCase(),
          page: requestedPage,
          ...workflowRequest,
        },
      })
    )
      .unwrap()
      .then((result) => {
        // The page from the URL no longer exists (e.g. documents were deleted) - go to the last one.
        if (requestedPage > 1 && (result.results?.length ?? 0) === 0 && isStillRequestedView()) {
          const lastPage = Math.max(1, Math.ceil((result.count || 0) / DOCUMENTS_PER_PAGE));
          if (lastPage < requestedPage) {
            updateVaultQuery({ page: lastPage }, { replace: true });
          }
        }
      })
      .catch(() => {
        // Out-of-range pages are rejected by the API - fall back to the first page.
        if (requestedPage > 1 && isStillRequestedView()) {
          updateVaultQuery({ page: 1 }, { replace: true });
        }
      });
  }, [
    isVaultPage,
    isVaultListVisible,
    currentUser.id,
    taskIds,
    vaultCategory,
    vaultPage,
    workflowRequest,
    updateVaultQuery,
    dispatch,
  ]);

  // Document Vault: normalize the URL (legacy ?activeTab=..., invalid values) without a new history entry.
  useEffect(() => {
    if (!isVaultPage) {
      return;
    }
    const canonical = writeVaultQuery(params, readVaultQuery(params));
    if (canonical.toString() !== params.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [isVaultPage, params, setSearchParams]);

  const getEmployees = useCallback(() => {
    if (company) {
      if (employeesLoading === 'idle') {
        dispatch(fetchEmployees({ company_id: company.id! }));
      }
    }
  }, [employeesLoading, company, dispatch]);

  const getCustomers = useCallback(() => {
    if (company) {
      if (customersLoading === 'idle') {
        dispatch(fetchCustomers({ company_id: company.id!, page_size: 100 }));
      }
    }
  }, [customersLoading, company, dispatch]);

  const getSuppliers = useCallback(() => {
    if (company) {
      if (suppliersLoading === 'idle') {
        dispatch(fetchSuppliers({ company_id: company.id!, page_size: 100 }));
      }
    }
  }, [suppliersLoading, company, dispatch]);

  const getBusinessContacts = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileContactById({ id: company.id! }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  const getBusinessDetails = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileById({ id: company.id! }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearDocuments());
    };
  }, [dispatch]);

  useEffect(() => {
    getEmployees();
  }, [getEmployees]);

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    getBusinessContacts();
  }, [getBusinessContacts]);

  useEffect(() => {
    getBusinessDetails();
  }, [getBusinessDetails]);

  const isDocumentListVaultVisible = isProfilePage
    ? isProfileListVisible
    : !(activeTab === 'All' && cardView);

  const pollingStateRef = useRef({
    request: {
      user: currentUser.id,
      category: '',
      page: currentPage,
    } as DocumentVaultSearchData['data'],
    taskIds,
    status: documentUploadsLoading,
  });

  useEffect(() => {
    pollingStateRef.current = {
      request: {
        user: currentUser.id,
        category: categoryValue
          ? categoryValue.toLowerCase()
          : activeTab === 'All'
            ? ''
            : activeTab.toLowerCase(),
        page: currentPage,
        ...workflowRequest,
        ...profileQueryRequest,
      },
      taskIds,
      status: documentUploadsLoading,
    };
  }, [
    currentUser.id,
    categoryValue,
    activeTab,
    currentPage,
    workflowRequest,
    profileQueryRequest,
    taskIds,
    documentUploadsLoading,
  ]);

  useEffect(() => {
    if (!currentUser.id || !isDocumentListVaultVisible) {
      return;
    }

    const intervalId = setInterval(() => {
      const { request, taskIds: currentTaskIds, status } = pollingStateRef.current;

      if (
        !currentTaskIds ||
        currentTaskIds.length > 0 ||
        DOCUMENTS_BUSY_STATUSES.includes(status)
      ) {
        return;
      }
      dispatch(fetchDocuments({ data: request, silent: true }));
    }, DOCUMENTS_POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [currentUser.id, isDocumentListVaultVisible, dispatch]);

  return {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    employeesLoading,
    suppliersLoading,
    customersLoading,
    businessProfileLoading,
    activeTab,
    setActiveTab,
    documentsCount,
    documentsPage: currentPage,
    getNextPage,
    getPrevPage,
    getRequestedPage,
    handleTabChange,
    cardView,
    setCardView,
    showWorkflowBreadcrumbs,
    setShowWorkflowBreadcrumbs,
  };
};

export { useDocuments };
